// src/contexts/LanguageContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react';
import { ar } from '../locales/ar';
import { en } from '../locales/en';
import { it } from '../locales/it';
import { fr } from '../locales/fr'; // Import French translations

type Language = 'ar' | 'en' | 'it' | 'fr'; // Add 'fr' to the Language type
type Translations = typeof ar;

type LanguageContextType = {
  language: Language;
  translations: Translations;
  setLanguage: (lang: Language) => void;
  direction: 'rtl' | 'ltr';
};

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  // Get initial language with auto-detection
  const [language, setLanguage] = useState<Language>(() => {
    // First check localStorage
    const saved = localStorage.getItem('preferredLanguage');
    if (saved === 'ar' || saved === 'en' || saved === 'it' || saved === 'fr') {
      return saved as Language;
    }

    // Then check browser language
    const browserLang = navigator.language.toLowerCase();

    // Match browser language to supported languages
    if (browserLang.startsWith('ar')) {
      return 'ar';
    } else if (browserLang.startsWith('it')) {
      return 'it';
    } else if (browserLang.startsWith('fr')) {
      // Add French detection
      return 'fr';
    }

    // Default to English
    return 'en';
  });

  const getTranslations = (lang: Language) => {
    switch (lang) {
      case 'en':
        return en;
      case 'it':
        return it;
      case 'fr': // Add French translations
        return fr;
      default:
        return ar;
    }
  };

  const translations = getTranslations(language);
  const direction = language === 'ar' ? 'rtl' : 'ltr';

  const handleSetLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem('preferredLanguage', lang);
  };

  // Update document direction and language whenever language changes
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = direction;
    // Add direction-specific classes to body
    document.body.classList.remove('rtl', 'ltr');
    document.body.classList.add(direction);
  }, [language, direction]);

  return (
    <LanguageContext.Provider
      value={{
        language,
        translations,
        setLanguage: handleSetLanguage,
        direction,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
